/**
 * How-To video category taxonomy.
 *
 * The eight top-level categories mirror Cendien's service lines. Each how-to
 * article is placed by its `seo.video.group` (top-level) and
 * `seo.video.category` (sub-category, used by the ERP line). Both the runtime
 * category pages and the build-time prerenderer resolve placement through the
 * helpers here so the two never drift.
 */
import type { ArticleVideo } from './articleVideo';
import { HOW_TO_SERIES } from './articleVideo';
import type { PageSEOMeta } from './seo-config';
import {
  fitHowToTitle,
  fitHowToDescription,
  fitDescription,
} from './dynamicSeo';

/** Base path for the how-to section. */
export const HOW_TO_ROOT = '/videos/how-tos';

export interface HowToSubCategory {
  slug: string;
  label: string;
  intro: string;
}

export interface HowToCategory {
  slug: string;
  label: string;
  intro: string;
  /** The service page this category belongs to (for the "How-to videos" strip). */
  servicePath: string;
  subCategories?: HowToSubCategory[];
}

export const HOW_TO_CATEGORIES: HowToCategory[] = [
  {
    slug: 'erp',
    label: 'ERP Solutions',
    intro:
      'Step-by-step ERP guides for Infor Lawson and CloudSuite, Workday, Oracle PeopleSoft and Microsoft Dynamics — the enterprise platforms Cendien supports every day.',
    servicePath: '/services/erp',
    subCategories: [
      {
        slug: 'infor-lawson',
        label: 'Infor Lawson',
        intro:
          'Practical guides for Infor Lawson and CloudSuite — payroll, tax updates, reporting and day-to-day operations.',
      },
      {
        slug: 'workday',
        label: 'Workday',
        intro:
          'Workday how-to videos. Workday is a platform we support — Cendien is an independent provider, not a Workday partner.',
      },
      {
        slug: 'oracle-peoplesoft',
        label: 'Oracle PeopleSoft',
        intro: 'PeopleSoft administration, upgrade and support walkthroughs.',
      },
      {
        slug: 'microsoft-dynamics',
        label: 'Microsoft Dynamics',
        intro: 'Microsoft Dynamics configuration and operations walkthroughs.',
      },
      {
        slug: 'erp-general',
        label: 'ERP (General)',
        intro: 'Cross-platform ERP guides that apply to any enterprise system.',
      },
    ],
  },
  {
    slug: 'staffing',
    label: 'IT Staffing & Placement',
    intro:
      'Practical guides for finding, screening and onboarding IT and ERP consultants — contract, staff augmentation and direct hire.',
    servicePath: '/services/staffing',
  },
  {
    slug: 'service-desk',
    label: 'IT Service Desk',
    intro:
      'How-to walkthroughs for help desk, ticketing and on-site field support operations.',
    servicePath: '/services/service-desk',
  },
  {
    slug: 'development',
    label: 'App Development',
    intro:
      'Step-by-step guides for building and maintaining custom web and enterprise applications.',
    servicePath: '/services/app/app-development',
  },
  {
    slug: 'ai',
    label: 'AI & Analytics',
    intro:
      'Short, practical walkthroughs for putting AI and analytics to work — safely, and with measurable value.',
    servicePath: '/services/ai',
  },
  {
    slug: 'cloud',
    label: 'Cloud',
    intro:
      'Guides for cloud migration, day-to-day operations and cost control across AWS, Azure and Google Cloud.',
    servicePath: '/services/cloud',
  },
  {
    slug: 'security',
    label: 'Cybersecurity & Compliance',
    intro:
      'Step-by-step guides for protecting systems and meeting compliance requirements like HIPAA, SOC 2 and CMMC.',
    servicePath: '/services/managed/security',
  },
  {
    slug: 'healthcare-it',
    label: 'Healthcare IT',
    intro: 'How-to guides for EHR, interoperability and health data operations.',
    servicePath: '/services/healthcare-it',
  },
];

const CATEGORY_BY_SLUG = new Map<string, HowToCategory>(
  HOW_TO_CATEGORIES.map((c) => [c.slug, c]),
);

const ERP_CATEGORY = CATEGORY_BY_SLUG.get('erp') || null;
const ERP_SUB_SLUGS = new Set<string>(
  (ERP_CATEGORY?.subCategories || []).map((s) => s.slug),
);

/** Common alternative spellings that map onto a canonical category slug. */
const GROUP_ALIASES: Record<string, string> = {
  'erp-solutions': 'erp',
  'erp-solution': 'erp',
  'it-staffing': 'staffing',
  'staffing-placement': 'staffing',
  'it-staffing-placement': 'staffing',
  'help-desk': 'service-desk',
  'it-service-desk': 'service-desk',
  'service-desk': 'service-desk',
  'app-development': 'development',
  'application-development': 'development',
  'development': 'development',
  'analytics': 'ai',
  'ai-analytics': 'ai',
  'ai-&-analytics': 'ai',
  'cloud-services': 'cloud',
  'cybersecurity': 'security',
  'cyber-security': 'security',
  'cybersecurity-compliance': 'security',
  'cybersecurity-&-compliance': 'security',
  'healthcare': 'healthcare-it',
  'health-it': 'healthcare-it',
};

export function getHowToCategory(slug: string): HowToCategory | null {
  return CATEGORY_BY_SLUG.get(slug) || null;
}

export function getHowToSubCategory(
  catSlug: string,
  subSlug: string,
): HowToSubCategory | null {
  const cat = CATEGORY_BY_SLUG.get(catSlug);
  return cat?.subCategories?.find((s) => s.slug === subSlug) || null;
}

/**
 * Topic chips — cross-cutting themes that are not service-line categories.
 * They sit alongside the service-line chips on the how-to index and filter by
 * the video's `seo.video.platforms` array and/or the article's `tags`.
 *
 * Chips are counted against the live list and hidden when they have zero
 * matches, so a new topic appears automatically once content is tagged.
 */
export interface HowToTopicChip {
  slug: string;
  label: string;
  /** Match when the video `seo.video.platforms` array contains one of these (case-insensitive). */
  platforms?: string[];
  /** Match when the article `tags` include one of these (case-insensitive). */
  tags?: string[];
}

export const HOW_TO_TOPIC_CHIPS: HowToTopicChip[] = [
  { slug: 'ai-agents', label: 'AI Agents', platforms: ['AI Agents'] },
  { slug: 'finance', label: 'Finance', tags: ['Finance'] },
  { slug: 'human-resources', label: 'Human Resources', tags: ['Human Resources'] },
  { slug: 'supply-chain', label: 'Supply Chain', tags: ['Supply Chain'] },
  { slug: 'migrations', label: 'Migrations', tags: ['Migration'] },
  { slug: 'implementations', label: 'Implementations', tags: ['Implementation'] },
  { slug: 'talent-management', label: 'Talent Management', tags: ['Talent Management'] },
  { slug: 'training', label: 'Training', tags: ['Training'] },
];

const TOPIC_BY_SLUG = new Map<string, HowToTopicChip>(
  HOW_TO_TOPIC_CHIPS.map((t) => [t.slug, t]),
);

/** True when a slug belongs to a topic chip (as opposed to a service category). */
export function isHowToTopicSlug(slug: string): boolean {
  return TOPIC_BY_SLUG.has(slug);
}

function lowerList(list?: string[] | null): string[] {
  return (list || [])
    .map((v) => String(v).trim().toLowerCase())
    .filter(Boolean);
}

/** True when a video matches a topic chip by its platforms and/or article tags. */
export function howToMatchesTopic(
  video: ArticleVideo | null | undefined,
  tags: string[] | undefined | null,
  topicSlug: string,
): boolean {
  const chip = TOPIC_BY_SLUG.get(topicSlug);
  if (!chip) return false;
  if (chip.platforms?.length) {
    const platforms = lowerList(video?.platforms);
    if (chip.platforms.some((p) => platforms.includes(p.toLowerCase()))) return true;
  }
  if (chip.tags?.length) {
    const tagList = lowerList(tags);
    if (chip.tags.some((t) => tagList.includes(t.toLowerCase()))) return true;
  }
  return false;
}

/** Builds the canonical path for a category (and optional sub-category). */
export function howToCategoryPath(catSlug: string, subSlug?: string): string {
  return subSlug ? `${HOW_TO_ROOT}/${catSlug}/${subSlug}` : `${HOW_TO_ROOT}/${catSlug}`;
}

function canonicalGroup(raw: string | undefined): string {
  const key = String(raw || '').trim().toLowerCase();
  if (!key) return '';
  if (GROUP_ALIASES[key]) return GROUP_ALIASES[key];
  if (CATEGORY_BY_SLUG.has(key)) return key;
  return '';
}

export function videoGroupKey(video?: ArticleVideo | null): string {
  return String(video?.group || '').trim().toLowerCase();
}

export function videoSubKey(video?: ArticleVideo | null): string {
  return String(video?.category || '').trim().toLowerCase();
}

/**
 * Resolves where a how-to video belongs: `{ group, sub }`. Falls back to
 * inferring the group from the sub-category when `group` is missing, and
 * defaults a bare ERP video to the `erp-general` sub-category.
 */
export function resolveHowToPlacement(video?: ArticleVideo | null): {
  group: string;
  sub: string;
} {
  const sub = videoSubKey(video);
  const group = canonicalGroup(videoGroupKey(video));
  if (group) {
    if (group === 'erp') return { group: 'erp', sub: sub || 'erp-general' };
    return { group, sub };
  }
  if (sub) {
    if (ERP_SUB_SLUGS.has(sub)) return { group: 'erp', sub };
    const top = canonicalGroup(sub);
    if (top) return { group: top, sub: '' };
  }
  return { group: '', sub: '' };
}

export function howToMatchesCategory(
  video: ArticleVideo | null | undefined,
  catSlug: string,
): boolean {
  return resolveHowToPlacement(video).group === catSlug;
}

export function howToMatchesSub(
  video: ArticleVideo | null | undefined,
  catSlug: string,
  subSlug: string,
): boolean {
  const p = resolveHowToPlacement(video);
  return p.group === catSlug && p.sub === subSlug;
}

export interface HowToArticle {
  id?: string | number;
  slug: string;
  title?: string;
  excerpt?: string;
  category?: string;
  date?: string;
  status?: string;
  tags?: string[];
  seo?: { video?: ArticleVideo };
  [key: string]: unknown;
}

/** True for a published article that carries a how-to series video. */
export function isHowToArticle(article: HowToArticle | null | undefined): boolean {
  if (!article) return false;
  if (article.status && article.status !== 'published') return false;
  const v = article?.seo?.video;
  return !!v && !!v.streamId && v.series === HOW_TO_SERIES;
}

function howToDate(article: HowToArticle): number {
  const t = article?.date ? new Date(article.date).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
}

/** Newest first, then by episode (descending). */
export function sortHowTos(list: HowToArticle[]): HowToArticle[] {
  return [...list].sort((a, b) => {
    const da = howToDate(a);
    const db = howToDate(b);
    if (db !== da) return db - da;
    const ea = Number(a.seo?.video?.episode) || 0;
    const eb = Number(b.seo?.video?.episode) || 0;
    return eb - ea;
  });
}

export function collectHowTos(articles: HowToArticle[]): HowToArticle[] {
  return sortHowTos((articles || []).filter(isHowToArticle));
}

export function filterHowTos(
  list: HowToArticle[],
  opts: { category?: string; subCategory?: string },
): HowToArticle[] {
  let out = list || [];
  const slug = opts?.category;
  if (slug) {
    // A topic chip filters by platforms/tags; anything else is a service group.
    if (TOPIC_BY_SLUG.has(slug)) {
      out = out.filter((a) => howToMatchesTopic(a.seo?.video, a.tags, slug));
    } else {
      out = out.filter((a) => howToMatchesCategory(a.seo?.video, slug));
    }
  }
  if (opts?.subCategory && slug && !TOPIC_BY_SLUG.has(slug)) {
    out = out.filter((a) =>
      howToMatchesSub(a.seo?.video, slug, opts.subCategory as string),
    );
  }
  return out;
}

/** Count of how-tos per top-level category slug. */
export function countByCategory(list: HowToArticle[]): Record<string, number> {
  const counts: Record<string, number> = {};
  (list || []).forEach((a) => {
    const g = resolveHowToPlacement(a.seo?.video).group;
    if (g) counts[g] = (counts[g] || 0) + 1;
  });
  return counts;
}

/** Count of how-tos per topic-chip slug (platforms + tags). */
export function countByTopic(list: HowToArticle[]): Record<string, number> {
  const counts: Record<string, number> = {};
  (list || []).forEach((a) => {
    HOW_TO_TOPIC_CHIPS.forEach((t) => {
      if (howToMatchesTopic(a.seo?.video, a.tags, t.slug)) {
        counts[t.slug] = (counts[t.slug] || 0) + 1;
      }
    });
  });
  return counts;
}

/** Count of how-tos per sub-category slug within one category. */
export function countBySubCategory(
  list: HowToArticle[],
  catSlug: string,
): Record<string, number> {
  const counts: Record<string, number> = {};
  (list || []).forEach((a) => {
    const p = resolveHowToPlacement(a.seo?.video);
    if (p.group === catSlug && p.sub) counts[p.sub] = (counts[p.sub] || 0) + 1;
  });
  return counts;
}

/** Page SEO for a category (or sub-category) page, or null when unknown. */
export function howToCategorySeo(
  catSlug: string,
  subSlug?: string,
): PageSEOMeta | null {
  const cat = CATEGORY_BY_SLUG.get(catSlug);
  if (!cat) return null;
  const sub = subSlug ? getHowToSubCategory(catSlug, subSlug) : null;
  if (subSlug && !sub) return null;
  const label = sub ? sub.label : cat.label;
  // Workday is a platform Cendien SUPPORTS as an independent provider — never
  // describe Cendien as a Workday partner.
  const isWorkday = catSlug === 'erp' && subSlug === 'workday';
  const description = isWorkday
    ? fitDescription(
        'Workday how-to videos from Cendien. Cendien consultants support Workday as an independent provider — not a Workday partner. Step-by-step guides.',
      )
    : fitHowToDescription(label);
  return {
    title: fitHowToTitle(label),
    description,
    keywords: `${label} how-to videos, ${label} tutorials, Cendien how-to videos, IT how-to videos`,
    schemaType: 'CollectionPage',
  };
}

/**
 * Page SEO for a topic-chip page (e.g. AI Agents). Topic chips filter the
 * how-to index today; this keeps their meta in range if a chip ever gets its
 * own route, using the same template engine as the service-line categories.
 */
export function howToTopicSeo(slug: string): PageSEOMeta | null {
  const topic = TOPIC_BY_SLUG.get(slug);
  if (!topic) return null;
  return {
    title: fitHowToTitle(topic.label),
    description: fitHowToDescription(topic.label),
    keywords: `${topic.label} how-to videos, ${topic.label} tutorials, Cendien how-to videos, IT how-to videos`,
    schemaType: 'CollectionPage',
  };
}

/** H1 + intro line for a category (or sub-category) page. */
export function howToCategoryHeading(
  catSlug: string,
  subSlug?: string,
): { h1: string; intro: string } | null {
  const cat = CATEGORY_BY_SLUG.get(catSlug);
  if (!cat) return null;
  const sub = subSlug ? getHowToSubCategory(catSlug, subSlug) : null;
  if (subSlug && !sub) return null;
  return {
    h1: `${sub ? sub.label : cat.label} How-To Videos`,
    intro: sub ? sub.intro : cat.intro,
  };
}